import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import {
  useRenderStore,
  useFormData,
  useSelectedReferences,
  useHasChanges,
  useIsValidForSubmit,
  useSelectedControlIds,
  useFormDataWithSeed,
  getExploreBaseline,
  getRefineBaseline,
} from './render-form.store';
import type { RenderPrompts } from '@/types/render';

// Re-export commonly used selectors
export {
  useFormData,
  useHasChanges,
  useIsValidForSubmit,
  useSelectedControlIds,
  useFormDataWithSeed,
};

export const useRenderSettingsState = () => {
  const formData = useFormData();
  const defaultValues = useRenderStore((state) => state.defaultValues);
  const selectedReferences = useSelectedReferences();

  const selectedControlIds = useMemo(
    () => formData.controls || [],
    [formData.controls]
  );

  return {
    formData,
    defaultValues,
    selectedReferences,
    selectedControlIds,
  };
};

export const useRenderSettingsActions = () => {
  return useRenderStore(
    useShallow((state) => ({
      updateFormData: state.updateFormData,
      setDefaultValues: state.setDefaultValues,
      resetFormData: state.resetFormData,
      initializeFormData: state.initializeFormData,
      resetChangesToDefaults: state.resetChangesToDefaults,
      setSelectedReferences: state.setSelectedReferences,
      reset: state.reset,
      setIsDerivative: state.setIsDerivative,
      generateRandomSeed: state.generateRandomSeed,
      generateNextSeed: state.generateNextSeed,
    }))
  );
};

export const useSectionChanges = () => {
  const formData = useFormData();
  const defaultValues = useRenderStore((state) => state.defaultValues);
  const parentRenderMode = useRenderStore((state) => state.parentRenderMode);
  const isDerivative = useRenderStore((state) => state.formData.is_derivative);

  return useMemo(() => {
    if (!defaultValues) return {};

    // Each mode has its own baseline for the change markers:
    // - Explore: anchored at the per-tab Explore baseline. Equal to
    //   `defaultValues` when the source render is Explore; blank when the
    //   source is Refine (RENDER FORM SPEC rule 1 — a Refine source must
    //   not seed Explore, otherwise every section would flag as changed).
    // - Refine: anchored at a blank form — refine is point edits, so
    //   anything the user adds counts as a change for THIS refine.
    const baseline =
      formData.mode === 'refine'
        ? {
            // Match the Refine starting point used by setRefineMode +
            // isEmptyRefine. Auto-refs default to "no refs" in Refine, so
            // toggling them ON is a user delta — handled inside
            // `getRefineBaseline()`.
            ...getRefineBaseline(defaultValues),
            seed: formData.seed, // seed is preserved across the toggle
          }
        : getExploreBaseline(defaultValues, parentRenderMode);

    // Deep equality check that handles null, undefined, and arrays properly
    const deepEqual = (a: unknown, b: unknown): boolean => {
      if (a === b) return true;
      if (a == null || b == null) return a === b;
      if (typeof a !== 'object' || typeof b !== 'object') return false;

      if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) return false;
        return a.every((item, index) => deepEqual(item, b[index]));
      }

      const keysA = Object.keys(a as object);
      const keysB = Object.keys(b as object);
      if (keysA.length !== keysB.length) return false;

      return keysA.every((key) =>
        deepEqual(
          (a as Record<string, unknown>)[key],
          (b as Record<string, unknown>)[key]
        )
      );
    };

    // Treat `null`, `undefined`, and `[]` as the same "no selection" sentinel
    // for array fields — the server, the form, and the refine baseline use
    // them interchangeably (e.g. server omits → undefined; user deselects →
    // null; some endpoints return []). Mirrors Apple `arraysEqual` (which
    // normalises nil ↔ [] inside `SectionChanges.compare`) and Android's
    // non-nullable `List<String>` model where `null` simply does not exist.
    const arraysEqual = (a: unknown, b: unknown): boolean => {
      const aEmpty = a == null || (Array.isArray(a) && a.length === 0);
      const bEmpty = b == null || (Array.isArray(b) && b.length === 0);
      if (aEmpty && bEmpty) return true;
      if (aEmpty !== bEmpty) return false;
      return deepEqual(a, b);
    };

    // Style/Materials/Colors rows aggregate three signals: the library
    // selection, the project-reference picks for that category, and the
    // "all refs ↔ no refs" toggle that flips the default reference pool.
    // Apple `SectionChanges.compare` and Android equivalent both include
    // all three; WEB used to compare only the library IDs which silently
    // missed reference-only edits and (combined with the baseline leak in
    // setRefineMode) lit the dot for unrelated Refine toggles.
    const hasStyleChanged = () =>
      !arraysEqual(formData.style_ids, baseline.style_ids) ||
      !arraysEqual(
        formData.style_project_reference_ids,
        baseline.style_project_reference_ids
      ) ||
      formData.use_auto_style_refs !== baseline.use_auto_style_refs;

    const hasMaterialsChanged = () =>
      !arraysEqual(formData.material_ids, baseline.material_ids) ||
      !arraysEqual(
        formData.material_project_reference_ids,
        baseline.material_project_reference_ids
      ) ||
      formData.use_auto_material_refs !== baseline.use_auto_material_refs;

    const hasColorsChanged = () =>
      !arraysEqual(formData.colors, baseline.colors) ||
      !arraysEqual(
        formData.color_project_reference_ids,
        baseline.color_project_reference_ids
      ) ||
      formData.use_auto_color_refs !== baseline.use_auto_color_refs;

    const hasOutputChanged = () =>
      formData.output_style_id !== baseline.output_style_id ||
      !arraysEqual(
        formData.output_project_reference_ids,
        baseline.output_project_reference_ids
      );

    const hasControlsChanged = () =>
      !arraysEqual(formData.controls, baseline.controls);

    // Prompts compare per-field with absent ≡ '' — a server snapshot may
    // carry only one key (e.g. {prompt_concept} without prompt_angle) while
    // the form always holds both, and a shape-sensitive deep-equal flagged
    // that as a phantom change. Mirrors Apple's nil ↔ "" normalisation in
    // `SectionChanges.compare`.
    const promptsEqual = (a?: RenderPrompts, b?: RenderPrompts): boolean =>
      (a?.prompt_concept ?? '') === (b?.prompt_concept ?? '') &&
      (a?.prompt_angle ?? '') === (b?.prompt_angle ?? '');

    // Special check for environment - handle 'NO_CHANGE' value properly
    const hasEnvironmentChanged = () => {
      const formEnv = formData.environment_setting || 'NO_CHANGE';
      const defaultEnv = baseline.environment_setting || 'NO_CHANGE';
      return formEnv !== defaultEnv;
    };

    // Calculate all possible changes
    const allChanges = {
      style: hasStyleChanged(),
      colors: hasColorsChanged(),
      materials: hasMaterialsChanged(),
      prompts: !promptsEqual(formData.prompts, baseline.prompts),
      structure: hasControlsChanged(),
      output: hasOutputChanged(),
      environment: hasEnvironmentChanged(),
      seed: formData.seed !== baseline.seed,
    };

    // For derivative renders, only return changes for relevant sections
    if (isDerivative) {
      return {
        prompts: allChanges.prompts,
        structure: allChanges.structure,
        seed: allChanges.seed,
      };
    }

    // For main renders, return all changes
    return allChanges;
  }, [formData, defaultValues, parentRenderMode, isDerivative]);
};
